import {
  AloftPrice,
  CraftsmanCostProps,
  ORDER_FEATURES,
  getCraftsmanCost,
  isDefined,
  isUndefined,
  schPositiveInt,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectImprovementEntityIds,
  selectImprovementsEntities,
} from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateBelowGradeFullBathCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (isUndefined(improvementsForTransaction) || isUndefined(identification)) {
    return undefined;
  }

  const {
    basement,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = improvementsForTransaction;
  const {
    address: { PostalCode: zipCode },
  } = identification;

  const fullBaths = basement?.fullBaths ?? schPositiveInt.parse(0);

  if (
    isUndefined(quality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1 ||
    isUndefined(zipCode) ||
    !ORDER_FEATURES.basement.hasFeature(improvementsForTransaction)
  ) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getFullBathroomsCost({
    fullBaths,
    quality,
    propertyType,
    zipCode,
  });
};

const getTotalOrBreakdownBelowGradeFullBathCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculateBelowGradeFullBathCost(
    costProps,
    improvements,
    propertyIdentification,
    id,
  );

  if (costOutput === undefined) {
    return AloftPrice.zero;
  }

  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalBelowGradeFullBathCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) =>
  getTotalOrBreakdownBelowGradeFullBathCost(
    costProps,
    improvements,
    propertyIdentification,
    id,
    'totalCost',
  );

export const selectBelowGradeFullBathCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getTotalBelowGradeFullBathCost,
);

export const selectZipOnlyBelowGradeFullBathCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownBelowGradeFullBathCost(
      costProps,
      improvements,
      propertyIdentification,
      id,
      'zipOnly',
    ),
);

export const selectBelowGradeFullBathCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectImprovementEntityIds,
  ],
  (costProps, improvements, propertyIdentification, improvementEntityIds) =>
    improvementEntityIds.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalBelowGradeFullBathCost(
        costProps,
        improvements,
        propertyIdentification,
        propID,
      );
      if (isDefined(cost)) acc[propID] = cost;
      return acc;
    }, {}),
);
